import { action, observable, makeObservable } from 'mobx';
import moment from 'moment';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
// Imported as a type: a plain import of a type used in a decorated signature trips isolatedModules
// with emitDecoratorMetadata on.
import type { BannerDisplayMode } from 'Modules/BannerMessages/Types/BannerMessage';

export interface IBannerMessageEntityAttributes extends IModelAttributes {
	disabled: boolean;
	text: string;
	startDate: Date;
	endDate: Date;
	displayMode: BannerDisplayMode;
	color: string;
	title?: string | null;
	icon?: string | null;
	priority: number;
	pinned: boolean;
	dismissible: boolean;
	showInBookingWizard: boolean;

	routeIds: string[];
}

@entity('BannerMessageEntity', 'Banner Message')
export default class BannerMessageEntity extends Model implements IBannerMessageEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<BannerMessageEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute<BannerMessageEntity, string>()
	public text: string;

	@observable
	@attribute<BannerMessageEntity, Date>()
	public startDate: Date;

	@observable
	@attribute<BannerMessageEntity, Date>()
	public endDate: Date;

	/**
	 * How {@link startDate} / {@link endDate} are interpreted — see the server-side
	 * `BannerDisplayMode`. Defaulted here so a banner from an older cached payload behaves the way it
	 * did before the mode existed.
	 */
	@observable
	@attribute<BannerMessageEntity, string>()
	public displayMode: BannerDisplayMode = 'MatchTripDates';

	@observable
	@attribute<BannerMessageEntity, number>()
	public color: string;

	@observable
	@attribute<BannerMessageEntity, string>()
	public title?: string | null;

	/** A Material Symbols icon name; the display falls back to a generic info icon when unset. */
	@observable
	@attribute<BannerMessageEntity, string>()
	public icon?: string | null;

	@observable
	@attribute<BannerMessageEntity, number>()
	public priority: number = 0;

	@observable
	@attribute<BannerMessageEntity, boolean>()
	public pinned: boolean = false;

	@observable
	@attribute<BannerMessageEntity, boolean>()
	public dismissible: boolean = false;

	@observable
	@attribute<BannerMessageEntity, boolean>()
	public showInBookingWizard: boolean = false;

	/**
	 * The routes this banner is displayed on, flattened from the BannerMessageRoute join rows by
	 * the server.
	 */
	@observable
	@attribute()
	public routeIds: string[] = [];

	constructor(attributes?: Partial<IBannerMessageEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IBannerMessageEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.text !== undefined) {
				this.text = attributes.text;
			}
			if (attributes.startDate !== undefined) {
				if (attributes.startDate === null) {
					this.startDate = attributes.startDate;
				} else {
					this.startDate = moment(attributes.startDate).toDate();
				}
			}
			if (attributes.endDate !== undefined) {
				if (attributes.endDate === null) {
					this.endDate = attributes.endDate;
				} else {
					this.endDate = moment(attributes.endDate).toDate();
				}
			}
			if (attributes.displayMode !== undefined) {
				this.displayMode = attributes.displayMode;
			}
			if (attributes.color !== undefined) {
				this.color = attributes.color;
			}
			if (attributes.title !== undefined) {
				this.title = attributes.title;
			}
			if (attributes.icon !== undefined) {
				this.icon = attributes.icon;
			}
			if (attributes.priority !== undefined) {
				this.priority = attributes.priority ?? 0;
			}
			if (attributes.pinned !== undefined) {
				this.pinned = attributes.pinned ?? false;
			}
			if (attributes.dismissible !== undefined) {
				this.dismissible = attributes.dismissible ?? false;
			}
			if (attributes.showInBookingWizard !== undefined) {
				this.showInBookingWizard = attributes.showInBookingWizard ?? false;
			}
			if (attributes.routeIds !== undefined) {
				this.routeIds = attributes.routeIds ?? [];
			}
		}
	}
}
